import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { VEHICLES, buildDegradationSeries, buildVehicleCatalog } from "./vehicles";
import { useTeslaLive } from "./useTeslaLive";
import { getEpaForVehicle } from "./teslaEpa";
import { initWarrantyRules, dbWarrantyFor } from "./brand";

const VehicleContext = createContext(null);
const STORAGE_KEY = "eviq.selected_vehicle_id";

/**
 * Match Tesla's live `{ model, year }` to the closest catalog vehicle so
 * derived math (EPA range, warranty threshold + miles + years, fleet avg)
 * uses the correct baseline for the user's actual car — not whatever demo
 * vehicle was selected before OAuth.
 */
function matchCatalogFromTesla(catalog, teslaModel, teslaYear) {
  if (!teslaModel) return null;
  const m = String(teslaModel).toLowerCase();
  const y = Number(teslaYear) || null;
  // Rank candidates by (model match) then (year proximity).
  const ranked = catalog
    .map((c) => {
      const cs = `${c.short || ""} ${c.name || ""} ${c.model || ""}`.toLowerCase();
      let modelScore = 0;
      if (m.includes("model 3") && cs.includes("model 3")) modelScore = 10;
      else if (m.includes("model y") && cs.includes("model y")) modelScore = 10;
      else if (m.includes("model s") && cs.includes("model s")) modelScore = 10;
      else if (m.includes("model x") && cs.includes("model x")) modelScore = 10;
      else if (m.includes("cybertruck") && cs.includes("cybertruck")) modelScore = 10;
      else if (m.includes("roadster") && cs.includes("roadster")) modelScore = 10;
      if (modelScore === 0) return null;
      const yearGap = y ? Math.abs((c.year || 0) - y) : 0;
      return { c, modelScore, yearGap };
    })
    .filter(Boolean)
    .sort((a, b) => b.modelScore - a.modelScore || a.yearGap - b.yearGap);
  return ranked[0]?.c || null;
}

export const VehicleProvider = ({ children }) => {
  // Warranty terms come from EVIQ's EV_BATTERY_WARRANTY database.
  useEffect(() => { initWarrantyRules(); }, []);

  const catalog = useMemo(() => {
    const dyn = buildVehicleCatalog();
    const legacy = VEHICLES.filter((v) => !dyn.some((d) => d.id === v.id));
    return [...legacy, ...dyn];
  }, []);

  const [vehicleId, setVehicleId] = useState(() => {
    if (typeof window === "undefined") return catalog[0].id;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && catalog.some((v) => v.id === saved)) return saved;
    return catalog[0].id;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, vehicleId);
    }
  }, [vehicleId]);

  // In-service (delivery) dates per VIN — warranty clocks count from the
  // true day one once the owner sets this (falls back to model year).
  const [inServiceDates, setInServiceDatesState] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("eviq.in_service_dates") || "{}"); }
    catch { return {}; }
  });
  const setInServiceDate = React.useCallback((vin, iso) => {
    setInServiceDatesState((prev) => {
      const next = { ...prev };
      if (iso) next[vin] = iso; else delete next[vin];
      try { window.localStorage.setItem("eviq.in_service_dates", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  // Measured degradation velocity from daily SoH snapshots (backend
  // /api/soh/snapshot). Stays null until ≥2 samples span ≥14 days.
  const [measuredVel, setMeasuredVel] = useState(null);

  const live = useTeslaLive();

  const value = useMemo(() => {
    // Start with the catalog vehicle (demo data)
    const demoBase = catalog.find((v) => v.id === vehicleId) || catalog[0];

    // Look up the currently-selected Tesla vehicle's PROFILE from the
    // /api/tesla/vehicles list. This is available as soon as the user
    // completes OAuth — even if the car is asleep and `vehicle_data`
    // hasn't returned yet.
    const teslaProfile =
      live.connected && Array.isArray(live.vehicles) && live.selectedId
        ? live.vehicles.find((v) => v.id === live.selectedId)
        : null;

    // Re-base derived math (catalog EPA, warranty miles/years/threshold,
    // fleet avg) on the correct catalog entry for the user's actual
    // Tesla — otherwise SoH % and warranty room compare against whatever
    // demo vehicle happened to be selected pre-OAuth.
    const baseVehicle = teslaProfile
      ? matchCatalogFromTesla(catalog, teslaProfile.model, teslaProfile.year) || demoBase
      : demoBase;

    let vehicle = baseVehicle;

    // Layer 1 — Tesla profile is available. Immediately replace vehicle
    // identity fields (year/model/VIN/display_name/color) so the header
    // and welcome banner show the user's real car, not the mock catalog.
    if (teslaProfile) {
      // `model` from the backend already includes the "Model " prefix
      // (e.g. "Model 3", "Model Y") — don't double-prefix it.
      const modelShort = teslaProfile.model || baseVehicle.short || baseVehicle.model;
      vehicle = {
        ...baseVehicle,
        live: true,
        liveAgeSec: 0,
        brand: teslaProfile.brand || "tesla",
        displayName: teslaProfile.display_name || null,
        name: teslaProfile.display_name
          ? `${teslaProfile.year || baseVehicle.year} ${modelShort} · ${teslaProfile.display_name}`
          : `${teslaProfile.year || baseVehicle.year} ${modelShort}`,
        short: modelShort,
        model: teslaProfile.model || baseVehicle.model,
        year: teslaProfile.year || baseVehicle.year,
        vin: teslaProfile.vin || baseVehicle.vin,
        color: teslaProfile.color || baseVehicle.color,
        state: teslaProfile.state || "unknown",
      };
    }

    // Layer 2 — full vehicle_data is available. Overlay live battery,
    // GPS, odometer, and derived SoH on top of the profile layer.
    if (live.connected && live.data && live.data.connected !== false) {
      const d = live.data;
      const batt = d.battery || {};
      const veh = d.vehicle || {};
      const loc = d.location || {};
      const config = veh.config || {};

      // Resolve the true EPA-when-new baseline for THIS Tesla via the
      // static Tesla EPA table (indexed by model × year × trim). This is
      // the number Tesla's Fleet API `battery_range` was compared against
      // by Tesla themselves when the car was new — dividing the current
      // rated range by this gives an honest SoH %.
      const epaRow = getEpaForVehicle({
        car_type: config.car_type,
        model: veh.model || vehicle.model,
        year: veh.year || vehicle.year,
        trim_badging: config.trim_badging,
        wheel_type: config.wheel_type,
        has_dual_motor: config.has_dual_motor,
        has_ludicrous_mode: config.has_ludicrous_mode,
        perf_config: config.perf_config,
      });
      const isFord = (config.brand || vehicle.brand) === "ford";
      const isVolvoBrand = (config.brand || vehicle.brand) === "volvo";
      // Ford/Volvo have no static EPA table — use the backend-supplied EPA hint.
      const trueEpa = (isFord || isVolvoBrand) && typeof config.epa_range_mi === "number"
        ? config.epa_range_mi
        : (epaRow?.epa_mi || baseVehicle.epaRange || 300);
      // Warranty terms: EVIQ's EV_BATTERY_WARRANTY database is the source
      // of truth; trim-exact EPA table (Tesla) or OEM baseline is only the
      // fallback until the DB rules load.
      const dbWarranty = dbWarrantyFor({
        brand: isFord ? "Ford" : isVolvoBrand ? "Volvo" : "Tesla",
        model: veh.model || vehicle.model,
        year: veh.year || vehicle.year,
        trim: config.trim_badging || "",
        preferMiles: epaRow?.warranty?.miles,
      });
      const trueWarranty = dbWarranty
        || (isFord || isVolvoBrand
          ? { years: 8, miles: 100000, threshold: 70 }
          : epaRow?.warranty
            ? {
                years: epaRow.warranty.years,
                miles: epaRow.warranty.miles,
                threshold: epaRow.warranty.threshold_pct || 70,
              }
            : baseVehicle.warranty);

      // Derive a real State of Health % from Tesla's own rated range.
      // `battery_range` (mapped to `range_epa_mi` in tesla_live.py) is
      // the EPA-adjusted rated range at current SoC — the same number
      // shown on the Tesla dashboard, which Tesla scales down as the
      // pack degrades. Scaling it up to 100% SoC gives the pack's
      // current rated full-charge range, which divided by the trim's
      // original EPA rating gives the true % of capacity retained.
      const rangeAtCurrentSoc =
        typeof batt.range_epa_mi === "number" && batt.range_epa_mi > 0
          ? batt.range_epa_mi
          : typeof batt.range_est_mi === "number" && batt.range_est_mi > 0
            ? batt.range_est_mi
            : null;
      const usablePct = batt.usable_level ?? batt.level;
      const scaledFullRange =
        typeof rangeAtCurrentSoc === "number" &&
        rangeAtCurrentSoc > 0 &&
        typeof usablePct === "number" &&
        usablePct > 5
          ? (rangeAtCurrentSoc * 100) / usablePct
          : null;
      // Age basis: owner-set in-service date beats model-year guess.
      const vinForAge = veh.vin || vehicle.vin;
      const inSvcIso = vinForAge ? inServiceDates[vinForAge] : null;
      const ageMonthsLive = inSvcIso
        ? Math.max(1, (Date.now() - Date.parse(inSvcIso)) / (30.44 * 86400e3))
        : Math.max(6, ((new Date().getFullYear()) - (veh.year || vehicle.year || new Date().getFullYear())) * 12);

      // Ford's xevBatteryRange (and Volvo's electricRange) is a driving-style
      // GOM estimate, NOT a rated range — deriving SoH from it inflates to a
      // misleading ~100%. Use an honest calendar+mileage aging estimate
      // instead until those OEMs expose a rated-capacity metric.
      let sohLive;
      let sohSuspect = false;
      if (isFord || isVolvoBrand) {
        const ageYears = ageMonthsLive / 12;
        const miles = typeof d.odometer?.miles === "number" ? d.odometer.miles : 0;
        // Sub-linear (√t) calendar fade + mild cycling term — matches the
        // observed NMC-pouch fleet curve (fast first-year loss, tapering).
        sohLive = +Math.max(85, Math.min(99, 100 - 4.2 * Math.sqrt(ageYears) - (miles / 1000) * 0.02)).toFixed(1);
      } else {
        sohLive = scaledFullRange
          ? Math.max(50, Math.min(100, +((scaledFullRange / trueEpa) * 100).toFixed(1)))
          : null;
        // Plausibility guardrail: a young pack reading far below the fleet's
        // worst-case fade almost always means a baseline (trim/wheel) mismatch,
        // not real degradation. Fall back to an honest age-based estimate and
        // flag it, rather than falsely predicting a warranty claim.
        if (typeof sohLive === "number") {
          const ageYears = ageMonthsLive / 12;
          if (ageYears < 4) {
            const minPlausible = 100 - (5 * Math.sqrt(ageYears) + 3);
            if (sohLive < minPlausible) {
              sohSuspect = true;
              const miles = typeof d.odometer?.miles === "number" ? d.odometer.miles : 0;
              sohLive = +Math.max(88, Math.min(99, 100 - 3.5 * Math.sqrt(ageYears) - (miles / 1000) * 0.015)).toFixed(1);
            }
          }
        }
      }

      vehicle = {
        ...vehicle,
        live: true,
        liveAgeSec: d.age_seconds ?? 0,
        brand: config.brand || vehicle.brand || "tesla",
        // Session id is passed through so downstream cards (PhantomDrain,
        // AI narrative, etc.) can hit backend endpoints keyed by the
        // current Tesla session without threading it manually.
        sessionId: live.sessionId || null,
        // Prefer the fresher vehicle_data profile if present.
        displayName: veh.display_name || vehicle.displayName || null,
        name: veh.display_name
          ? `${veh.year || vehicle.year} ${veh.model || vehicle.model} · ${veh.display_name}`
          : vehicle.name,
        model: veh.model || vehicle.model,
        year: veh.year || vehicle.year,
        vin: veh.vin || vehicle.vin,
        color: veh.color || vehicle.color,
        state: veh.state || vehicle.state,
        // Real trim + EPA baseline pulled from Tesla's OWN vehicle_config
        trim: epaRow?.trim || null,
        epaKey: epaRow?.key || null,
        epaRange: trueEpa,
        // Live rated range scaled to 100% SoC (i.e. what THIS car would
        // display at full charge right now, accounting for pack degradation).
        // Used by the Aero Modifications card to base its projections on
        // the owner's actual pack rather than a pristine EPA figure.
        liveRangeAt100: scaledFullRange ? Math.round(scaledFullRange) : null,
        packKwh: ((isFord || isVolvoBrand) && typeof config.pack_kwh === "number" ? config.pack_kwh : null) || epaRow?.pack_kwh || baseVehicle.packKwh || null,
        warranty: trueWarranty,
        // Raw config passed through so the UI can render wheel/trim
        // details without re-fetching (e.g. the VehicleBaselineStrip).
        config: config,
        // Live pack readings
        batteryPct: batt.level ?? baseVehicle.batteryPct,
        // Low-voltage (12V/16V) battery — measured when telemetry exposes it
        lvVoltage: typeof batt.lv_voltage === "number" ? batt.lv_voltage : null,
        usableBatteryPct: batt.usable_level ?? batt.level ?? baseVehicle.batteryPct,
        rangeEst: batt.range_est_mi ?? null,
        chargingState: batt.charging_state || null,
        chargeLimit: batt.charge_limit ?? null,
        timeToFullHours: batt.time_to_full_hours ?? null,
        // Tesla firmware version (car_version from vehicle_state) — powers
        // the firmware tracker chip in CareView.
        firmwareVersion: d.firmware?.version || null,
        // Live odometer + location
        odometer: d.odometer?.miles ?? baseVehicle.odometer,
        location: {
          lat: loc.lat,
          lng: loc.lng,
          heading: loc.heading,
          speedMph: loc.speed_mph,
          gpsAsOf: loc.gps_as_of,
        },
        // Live SoH — computed above against the trim-correct EPA baseline
        sohCurrent: sohLive ?? baseVehicle.sohCurrent,
        // Climate telemetry from Tesla (inside + outside cabin temp, °C)
        outsideTempC: d.climate?.outside_c ?? null,
        insideTempC: d.climate?.inside_c ?? null,
        // Phantom-drain contributors — read straight from vehicle_state
        sentryEnabled: d.sentry?.enabled ?? null,
        sentryAvailable: d.sentry?.available ?? null,
        cabinOverheatEnabled: d.cabin_overheat?.enabled ?? null,
        cabinOverheatState: d.cabin_overheat?.state ?? null,
        // Fleet-avg is still catalog-based (no live cohort endpoint yet);
        // deltaSoH tells the UI to hide the "vs fleet" comparison if we
        // truly have live SoH but no fleet number for the exact trim.
        fleetAvg: baseVehicle.fleetAvg,
        // 30-day velocity: measured history first (daily SoH snapshots),
        // else derived from the pack's own condition via the √t fade model
        //   loss(t) = K√t  ⇒  rate(t) = lossSoFar / (2 · ageMonths)
        // — never the demo catalog's fabricated number.
        delta30d: (() => {
          if (measuredVel && measuredVel.vin === vinForAge && measuredVel.velocity > 0.02) {
            return +measuredVel.velocity.toFixed(3);
          }
          if (typeof sohLive === "number") {
            const lossSoFar = Math.max(0, 100 - sohLive);
            return +Math.min(0.45, Math.max(0.08, lossSoFar / (2 * ageMonthsLive))).toFixed(3);
          }
          return baseVehicle.delta30d;
        })(),
        velocitySource:
          measuredVel && measuredVel.vin === vinForAge && measuredVel.velocity > 0.02
            ? "measured"
            : "model",
        sohIsLive: typeof sohLive === "number",
        // True when the range-derived SoH failed the plausibility check for a
        // young pack (likely EPA trim/wheel baseline mismatch) and an
        // age-based estimate was substituted.
        sohSuspect,
        inServiceDate: inSvcIso || null,
        ageYears: +(ageMonthsLive / 12).toFixed(2),
        currentRange: rangeAtCurrentSoc ?? baseVehicle.currentRange,
      };
    }

    const degradation = buildDegradationSeries(vehicle);
    return {
      vehicle,
      vehicleId,
      setVehicleId,
      setInServiceDate,
      vehicles: catalog,
      degradation,
      // Live-data channel — pass through so views can render "LIVE" badges,
      // refresh buttons, error toasts, and multi-vehicle pickers.
      live,
    };
  }, [vehicleId, catalog, live, inServiceDates, measuredVel, setInServiceDate]);

  // Record a daily SoH snapshot (backend upserts by vin+date) and pick up
  // the measured velocity once enough history exists. Skipped for demo
  // sessions so fake VINs never pollute soh_history.
  useEffect(() => {
    const v = value.vehicle;
    if (!v?.live || !v?.vin || !v?.sohIsLive || typeof v.sohCurrent !== "number") return;
    const sid = window.localStorage.getItem("eviq.tesla_session_id") || "";
    if (sid.startsWith("ford-demo")) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    fetch(`${API}/api/soh/snapshot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        vin: v.vin,
        soh_pct: v.sohCurrent,
        odometer_mi: typeof v.odometer === "number" ? v.odometer : null,
        brand: v.brand || "tesla",
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (j && typeof j.velocity_30d === "number") {
          setMeasuredVel({ vin: v.vin, velocity: j.velocity_30d, samples: j.samples, spanDays: j.span_days });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.vehicle?.vin, value.vehicle?.live, value.vehicle?.sohIsLive]);

  return (
    <VehicleContext.Provider value={value}>{children}</VehicleContext.Provider>
  );
};

export function useVehicle() {
  const ctx = useContext(VehicleContext);
  if (!ctx) throw new Error("useVehicle must be used inside <VehicleProvider>");
  return ctx;
}
